import React, { useEffect, useMemo, useState } from 'react';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonInput,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  IonButton,
  IonModal,
  IonAlert,
  IonSegment,
  IonSegmentButton,
  IonSearchbar,
  IonDatetime,
  useIonToast,
} from '@ionic/react';
import { useParams, useHistory } from 'react-router-dom';
import {
  useProcessedMaterialDetail,
  useProcessedMaterials,
  useManufacturingHistoryForMaterial,
} from '../hooks/useManufacturing';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ManufacturingHistoryWithDetails,
  ManufacturingRecipeWithDetails,
  IRawMaterial,
} from 'shared';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import {
  formatCurrency,
  formatDecimal,
  formatDateTime as formatDateTimeDisplay,
} from '../utils/format';

// Function to format date as DD/MM/YYYY
const formatDate = (dateInput: string | Date): string => {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};
import apiClient from '../api/client';

interface Params {
  id: string;
}

const ProcessedMaterialDetail: React.FC = () => {
  const { id } = useParams<Params>();
  const history = useHistory();
  const { t, language } = useLanguage();
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';

  const {
    processedMaterialsLoading,
    updateHistoryMutation,
    deleteHistoryMutation,
    deleteProcessedMaterialMutation,
  } = useProcessedMaterials();
  const detailQuery = useProcessedMaterialDetail(id);
  const historyQuery = useManufacturingHistoryForMaterial(id);
  const [editingEntry, setEditingEntry] =
    useState<ManufacturingHistoryWithDetails | null>(null);
  const [editQuantity, setEditQuantity] = useState<string>('');
  const [editManufacturedDate, setEditManufacturedDate] = useState<string>('');
  const [deletingEntry, setDeletingEntry] =
    useState<ManufacturingHistoryWithDetails | null>(null);
  const [showDeleteMaterialAlert, setShowDeleteMaterialAlert] = useState(false);
  const [deleteMaterialError, setDeleteMaterialError] = useState<string | null>(null);
  const [historyFilter, setHistoryFilter] = useState<'all' | '7' | '30'>('all');

  const filteredHistory = useMemo(() => {
    const entries = historyQuery.data ?? [];
    if (historyFilter === 'all') {
      return entries;
    }
    const days = historyFilter === '7' ? 7 : 30;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return entries.filter((entry) => {
      const manufacturedAt = new Date(entry.manufacturedAt);
      return manufacturedAt >= cutoff;
    });
  }, [historyFilter, historyQuery.data]);

  const queryClient = useQueryClient();
  const [presentToast] = useIonToast();
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [selectedRecipeMaterialId, setSelectedRecipeMaterialId] = useState<string>('');
  const [recipeQuantity, setRecipeQuantity] = useState('');
  const [editingRecipe, setEditingRecipe] = useState<ManufacturingRecipeWithDetails | null>(null);
  const [recipeToDelete, setRecipeToDelete] = useState<ManufacturingRecipeWithDetails | null>(null);

  useEffect(() => {
    if (!id) {
      history.replace('/tabs/manufacturing');
    }
  }, [history, id]);

  const rawMaterialsQuery = useQuery({
    queryKey: ['raw-materials'],
    queryFn: async () => {
      const { data } = await apiClient.get<IRawMaterial[]>('/materials');
      return data;
    },
  });

  const filteredRawMaterials = useMemo(() => {
    const materials = rawMaterialsQuery.data ?? [];
    const term = recipeSearch.trim().toLowerCase();
    if (!term) {
      return materials;
    }
    return materials.filter((materialItem) =>
      materialItem.name.toLowerCase().includes(term),
    );
  }, [rawMaterialsQuery.data, recipeSearch]);

  const material = detailQuery.data ?? null;
  const materialId = material?.id ?? null;
  const stock = formatDecimal(Number(material?.currentStock ?? 0), {
    locale,
    maximumFractionDigits: 6,
  });
  const minStock = formatDecimal(Number(material?.minStockLevel ?? 0), {
    locale,
    maximumFractionDigits: 6,
  });
  const recipes = material?.manufacturingRecipes ?? [];
  const selectedRecipeMaterial = useMemo(() => {
    if (!selectedRecipeMaterialId) {
      return null;
    }
    return (rawMaterialsQuery.data ?? []).find((materialItem) => materialItem.id === selectedRecipeMaterialId) || null;
  }, [rawMaterialsQuery.data, selectedRecipeMaterialId]);
  const isLoading = processedMaterialsLoading || detailQuery.isLoading;

  const resetRecipeModal = () => {
    setIsRecipeModalOpen(false);
    setRecipeSearch('');
    setSelectedRecipeMaterialId('');
    setRecipeQuantity('');
    setEditingRecipe(null);
  };

  const addRecipeMutation = useMutation({
    mutationFn: async ({ rawMaterialId, quantity, previousRecipeId }: { rawMaterialId: string; quantity: number; previousRecipeId?: string }) => {
      if (!materialId) {
        throw new Error('Processed material not loaded');
      }
      if (previousRecipeId) {
        await apiClient.delete(`/processed-materials/manufacturing-recipes/${previousRecipeId}`);
      }
      await apiClient.post('/processed-materials/manufacturing-recipes', {
        processedMaterialId: materialId,
        rawMaterialId,
        quantityNeeded: quantity,
      });
    },
    onSuccess: async (_, variables) => {
      const invalidatePromises = [
        queryClient.invalidateQueries({ queryKey: ['processed-materials'] }),
      ];
      if (materialId) {
        invalidatePromises.push(
          queryClient.invalidateQueries({ queryKey: ['processed-material', materialId] }),
        );
      }
      await Promise.all(invalidatePromises);
      presentToast({
        message: variables.previousRecipeId
          ? t('ingredientUpdated') ?? 'Ingredient updated'
          : t('ingredientAdded') ?? 'Ingredient added',
        duration: 2000,
        color: 'success',
      });
      resetRecipeModal();
    },
    onError: () => {
      presentToast({
        message: t('failedToSaveIngredient') ?? 'Failed to save ingredient',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const deleteRecipeMutation = useMutation({
    mutationFn: async (recipeId: string) => {
      if (!materialId) {
        throw new Error('Processed material not loaded');
      }
      await apiClient.delete(`/processed-materials/manufacturing-recipes/${recipeId}`);
    },
    onSuccess: async () => {
      const invalidatePromises = [
        queryClient.invalidateQueries({ queryKey: ['processed-materials'] }),
      ];
      if (materialId) {
        invalidatePromises.push(
          queryClient.invalidateQueries({ queryKey: ['processed-material', materialId] }),
        );
      }
      await Promise.all(invalidatePromises);
      presentToast({
        message: t('ingredientDeleted') ?? 'Ingredient deleted',
        duration: 2000,
        color: 'success',
      });
      setRecipeToDelete(null);
    },
    onError: () => {
      presentToast({
        message: t('failedToDeleteIngredient') ?? 'Failed to delete ingredient',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const handleRecipeSave = async () => {
    const quantityNumber = Number(recipeQuantity);
    if (!selectedRecipeMaterialId) {
      presentToast({
        message: t('selectMaterial') ?? 'Select material',
        duration: 2000,
        color: 'warning',
      });
      return;
    }
    if (Number.isNaN(quantityNumber) || quantityNumber <= 0) {
      presentToast({
        message: t('enterQuantity') ?? 'Enter quantity',
        duration: 2000,
        color: 'warning',
      });
      return;
    }

    if (!materialId) {
      presentToast({
        message: t('failedToSaveIngredient') ?? 'Failed to save ingredient',
        duration: 2000,
        color: 'danger',
      });
      return;
    }

    addRecipeMutation.mutate({
      rawMaterialId: selectedRecipeMaterialId,
      quantity: quantityNumber,
      previousRecipeId: editingRecipe?.id,
    });
  };

  const openRecipeModal = (recipe?: ManufacturingRecipeWithDetails) => {
    if (recipe) {
      setEditingRecipe(recipe);
      setSelectedRecipeMaterialId(recipe.rawMaterialId);
      setRecipeQuantity(String(Number(recipe.quantityNeeded || 0)));
    } else {
      setEditingRecipe(null);
      setSelectedRecipeMaterialId('');
      setRecipeQuantity('');
    }
    setRecipeSearch('');
    setIsRecipeModalOpen(true);
  };
  const handleDeleteMaterial = async () => {
    setDeleteMaterialError(null);
    if (!materialId) {
      setDeleteMaterialError(
        t('failedToDeleteProcessedMaterial') ?? 'Failed to delete processed material',
      );
      setShowDeleteMaterialAlert(false);
      return;
    }
    try {
      await deleteProcessedMaterialMutation.mutateAsync(materialId);
      setShowDeleteMaterialAlert(false);
      history.replace('/tabs/manufacturing');
    } catch (error) {
      setDeleteMaterialError(
        t('failedToDeleteProcessedMaterial') ?? 'Failed to delete processed material',
      );
      setShowDeleteMaterialAlert(false);
    }
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/manufacturing" />
          </IonButtons>
          <IonTitle>{material?.name ?? t('manufactured')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent
        fullscreen
        className={isLoading ? undefined : 'ion-padding'}
      >
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <IonSpinner />
          </div>
        ) : !material ? (
          <IonText color="medium">
            <p>{t('noData')}</p>
          </IonText>
        ) : (
          <>
            <section style={{ marginBottom: 24 }}>
              <IonList>
                <IonItem>
                  <IonLabel>
                    <p>{t('currentStock')}</p>
                    <h3>
                      {stock} {material.unit}
                    </h3>
                  </IonLabel>
                </IonItem>
                <IonItem>
                  <IonLabel>
                    <p>{t('minStockLevel')}</p>
                    <h3>
                      {minStock} {material.unit}
                    </h3>
                  </IonLabel>
                </IonItem>
                <IonItem>
                  <IonLabel>
                    <p>{t('costPerUnit')}</p>
                    <h3>{formatCurrency(Number(material.costPerUnit || 0), locale)}</h3>
                  </IonLabel>
                </IonItem>
                <IonItem>
                  <IonLabel>
                    <p>{t('recipeYieldLabel')}</p>
                    <h3>{formatDecimal(Number(material.recipeYield || 0), { locale, maximumFractionDigits: 2 })}</h3>
                  </IonLabel>
                </IonItem>
              </IonList>
            </section>

            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <IonButton
                expand="block"
                style={{ flex: 1 }}
                onClick={() => history.push(`/tabs/manufacturing/${material.id}/edit`)}
              >
                {t('editProcessedMaterial') ?? t('edit')}
              </IonButton>
              <IonButton
                expand="block"
                color="danger"
                style={{ flex: 1 }}
                onClick={() => {
                  setDeleteMaterialError(null);
                  setShowDeleteMaterialAlert(true);
                }}
                disabled={deleteProcessedMaterialMutation.isPending}
              >
                {deleteProcessedMaterialMutation.isPending ? t('deleting') ?? 'Deleting…' : t('delete')}
              </IonButton>
            </div>
            {deleteMaterialError ? (
              <IonText color="danger" style={{ display: 'block', marginBottom: 16 }}>
                <p>{deleteMaterialError}</p>
              </IonText>
            ) : null}

            <section style={{ marginBottom: 24 }}>
              <IonText color="primary">
                <h2 style={{ marginBottom: 12 }}>{t('manufacturingRecipe')}</h2>
              </IonText>
              <IonButton
                expand="block"
                fill="outline"
                onClick={() => openRecipeModal()}
                style={{ marginBottom: 12 }}
              >
                {t('addIngredients')}
              </IonButton>
              {recipes.length === 0 ? (
                <IonText color="medium">
                  <p>{t('noIngredientsYet')}</p>
                </IonText>
              ) : (
                <IonList>
                  {recipes.map((recipe) => (
                    <IonItem key={recipe.id}>
                      <IonLabel>
                        <h3>{recipe.rawMaterial?.name || t('unknown')}</h3>
                        <p>
                          {t('quantity')}: {formatDecimal(Number(recipe.quantityNeeded || 0), {
                            locale,
                            maximumFractionDigits: 6,
                          })}{' '}
                          {recipe.rawMaterial?.unit || ''}
                        </p>
                      </IonLabel>
                      <IonButtons slot="end">
                        <IonButton
                          size="small"
                          onClick={() => openRecipeModal(recipe)}
                        >
                          {t('edit')}
                        </IonButton>
                        <IonButton
                          size="small"
                          color="danger"
                          onClick={() => setRecipeToDelete(recipe)}
                          disabled={deleteRecipeMutation.isPending}
                        >
                          {t('delete')}
                        </IonButton>
                      </IonButtons>
                    </IonItem>
                  ))}
                </IonList>
              )}
            </section>

            <IonButton
              expand="block"
              onClick={() => history.push(`/tabs/manufacturing/${material.id}/produce`)}
            >
              {t('manufacture')}
            </IonButton>

            <section style={{ marginTop: 24 }}>
              <IonText color="primary">
                <h2 style={{ marginBottom: 12 }}>{t('manufacturingHistory')}</h2>
              </IonText>
              <IonSegment
                value={historyFilter}
                onIonChange={(event) => {
                  const value = (event.detail.value as 'all' | '7' | '30' | undefined) ?? 'all';
                  setHistoryFilter(value);
                }}
                style={{ marginBottom: 12 }}
              >
                <IonSegmentButton value="all">
                  <IonLabel>{t('all') ?? 'All'}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="7">
                  <IonLabel>{t('last7Days') ?? 'Last 7 days'}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="30">
                  <IonLabel>{t('last30Days') ?? 'Last 30 days'}</IonLabel>
                </IonSegmentButton>
              </IonSegment>
              {historyQuery.isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                  <IonSpinner />
                </div>
              ) : filteredHistory.length > 0 ? (
                <IonList>
                  {filteredHistory.map((entry) => (
                    <IonItem key={entry.id ?? `${entry.processedMaterialId}-${entry.manufacturedAt}`}>
                      <IonLabel>
                        <h3>{formatDate(entry.manufacturedAt)}</h3>
                        <p>
                          {t('quantityProduced')}: {formatDecimal(Number(entry.quantityProduced || 0), {
                            locale,
                            maximumFractionDigits: 6,
                          })}{' '}
                          {material.unit}
                        </p>
                        <p>
                          {t('totalCost')}: {formatCurrency(Number(entry.totalCost || 0), locale)}
                        </p>
                      </IonLabel>
                      <IonButtons slot="end">
                        <IonButton
                          size="small"
                          onClick={() => {
                            setEditingEntry(entry);
                            setEditQuantity(String(Number(entry.quantityProduced || 0)));
                            setEditManufacturedDate(entry.manufacturedAt.toISOString());
                          }}
                        >
                          {t('edit')}
                        </IonButton>
                        <IonButton
                          size="small"
                          color="danger"
                          onClick={() => setDeletingEntry(entry)}
                        >
                          {t('delete')}
                        </IonButton>
                      </IonButtons>
                    </IonItem>
                  ))}
                </IonList>
              ) : (
                <IonText color="medium">
                  <p>{t('noHistory')}</p>
                </IonText>
              )}
            </section>
          </>
        )}
      </IonContent>
      <IonModal isOpen={isRecipeModalOpen} onDidDismiss={resetRecipeModal}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              {editingRecipe ? t('editIngredient') ?? t('edit') : t('addIngredients')}
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={resetRecipeModal}>{t('cancel')}</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          {rawMaterialsQuery.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
              <IonSpinner />
            </div>
          ) : rawMaterialsQuery.data && rawMaterialsQuery.data.length > 0 ? (
            <>
              <IonSearchbar
                value={recipeSearch}
                onIonChange={(event) => setRecipeSearch(event.detail.value ?? '')}
                placeholder={t('search') ?? 'Search'}
              />
              <IonList>
                {filteredRawMaterials.map((materialOption) => (
                  <IonItem
                    key={materialOption.id}
                    button
                    detail={false}
                    onClick={() => setSelectedRecipeMaterialId(materialOption.id)}
                  >
                    <IonLabel>
                      <h3>{materialOption.name}</h3>
                      <p>{materialOption.unit}</p>
                    </IonLabel>
                    {selectedRecipeMaterialId === materialOption.id ? (
                      <IonText color="primary">{t('selected') ?? 'Selected'}</IonText>
                    ) : null}
                  </IonItem>
                ))}
              </IonList>
              <IonItem>
                <IonLabel position="stacked">{t('quantity')}</IonLabel>
                <IonInput
                  type="number"
                  value={recipeQuantity}
                  onIonChange={(event) => setRecipeQuantity(event.detail.value ?? '')}
                />
              </IonItem>
              {selectedRecipeMaterial ? (
                <IonText color="medium">
                  <p style={{ marginTop: 8 }}>
                    {t('unit')}: {selectedRecipeMaterial.unit}
                  </p>
                </IonText>
              ) : null}
              <IonButton
                expand="block"
                style={{ marginTop: 24 }}
                onClick={handleRecipeSave}
                disabled={addRecipeMutation.isPending}
              >
                {addRecipeMutation.isPending ? t('saving') ?? 'Saving…' : t('save')}
              </IonButton>
            </>
          ) : (
            <IonText color="medium">
              <p>{t('noData')}</p>
            </IonText>
          )}
        </IonContent>
      </IonModal>

      <IonAlert
        isOpen={Boolean(recipeToDelete)}
        onDidDismiss={() => setRecipeToDelete(null)}
        header={t('deleteIngredient') ?? 'Delete ingredient'}
        message={recipeToDelete?.rawMaterial?.name || ''}
        buttons={[
          {
            text: t('cancel'),
            role: 'cancel',
            handler: () => setRecipeToDelete(null),
          },
          {
            text: t('delete'),
            role: 'destructive',
            handler: () => {
              if (recipeToDelete) {
                deleteRecipeMutation.mutate(recipeToDelete.id);
              }
            },
          },
        ]}
      />

      <IonModal
        isOpen={Boolean(editingEntry)}
        onDidDismiss={() => setEditingEntry(null)}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('quantityProduced')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setEditingEntry(null)}>
                {t('cancel')}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonList>
            <IonItem>
              <IonLabel position="stacked">{t('manufacturedDate')}</IonLabel>
              <IonDatetime
                value={editManufacturedDate}
                onIonChange={(event) => {
                  const dateValue = Array.isArray(event.detail.value) ? event.detail.value[0] : event.detail.value;
                  setEditManufacturedDate(dateValue || '');
                }}
                presentation="date"
                max={new Date().toISOString()}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">{t('quantityProduced')}</IonLabel>
              <IonInput
                type="number"
                value={editQuantity}
                onIonChange={(event) => setEditQuantity(event.detail.value ?? '')}
              />
            </IonItem>
          </IonList>
          <IonButton
            expand="block"
            onClick={async () => {
              if (!editingEntry) return;
              const quantityValue = Number(editQuantity);
              if (Number.isNaN(quantityValue) || quantityValue < 0) {
                return;
              }
              if (!editManufacturedDate) {
                presentToast({
                  message: t('manufacturedDateRequired') ?? 'Manufactured date is required',
                  duration: 2000,
                  color: 'warning',
                });
                return;
              }
              await updateHistoryMutation.mutateAsync({
                id: editingEntry.id,
                quantityProduced: quantityValue,
                manufacturedAt: editManufacturedDate,
              });
              setEditingEntry(null);
            }}
            disabled={updateHistoryMutation.isPending}
          >
            {updateHistoryMutation.isPending ? t('saving') ?? 'Saving…' : t('save')}
          </IonButton>
        </IonContent>
      </IonModal>
      <IonAlert
        isOpen={showDeleteMaterialAlert}
        onDidDismiss={() => setShowDeleteMaterialAlert(false)}
        header={t('confirmDeleteProcessedMaterial') ?? 'Delete processed material?'}
        message={
          t('deleteProcessedMaterialMessage') ??
          'This will remove the processed material and its manufacturing history.'
        }
        buttons={[
          {
            text: t('cancel'),
            role: 'cancel',
            handler: () => setShowDeleteMaterialAlert(false),
          },
          {
            text: t('delete'),
            role: 'destructive',
            handler: async () => {
              await handleDeleteMaterial();
            },
          },
        ]}
      />
      <IonAlert
        isOpen={Boolean(deletingEntry)}
        onDidDismiss={() => setDeletingEntry(null)}
        header={t('confirmDeleteHistory') ?? 'Delete entry?'}
        message={
          deletingEntry
            ? formatDate(deletingEntry.manufacturedAt.toISOString())
            : ''
        }
        buttons={[
          {
            text: t('cancel'),
            role: 'cancel',
            handler: () => setDeletingEntry(null),
          },
          {
            text: t('delete'),
            role: 'destructive',
            handler: async () => {
              if (!deletingEntry) return;
              await deleteHistoryMutation.mutateAsync(deletingEntry.id);
              setDeletingEntry(null);
            },
          },
        ]}
      />
    </IonPage>
  );
};

export default ProcessedMaterialDetail;
